import React, {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {MessageCircle} from "react-feather";
import {openViafouraConversation} from "./viafouraConversation";
import {useViafouraCommentCount} from "./useViafouraCommentCount";
import {TrackingFunction, useViafouraInteractive} from "../LiveblogRenderContext";

export interface ViafouraPreviewProps {
  containerId: string;
  position?: number;
  trackingFunction?: TrackingFunction;
  /**
   * Render the card, but not as something that can be opened.
   *
   * Used by the editor preview. Viafoura resolves which account a conversation belongs to from the
   * **hostname of the page** the widget runs on — measured, not assumed: with the page on one host
   * and `og:url` pointing elsewhere, the lookup follows the host. The editor runs on a Tickaroo
   * domain, which is deliberately not provisioned with Viafoura, so a conversation opened there can
   * never be the customer's. An enabled trigger that opens a permanently empty overlay is worse than
   * one that is visibly unavailable, so the editor gets the card without the action (design D22).
   */
  disabled?: boolean;
}

/**
 * Per-post entry point into the shared Viafoura conversation overlay.
 *
 * The card carries a live comment count when Viafoura will tell us one (see
 * `viafouraCommentCounts`), and silently omits it otherwise — an un-provisioned domain, a blocked
 * request, or a conversation nobody has posted in yet all render as the bare trigger this card was
 * before counts existed.
 *
 * There are still deliberately no comment excerpts: those would need the comment bodies, which is a
 * different API and a different privacy question from a single integer.
 */
export function ViafouraPreview({containerId, position, trackingFunction, disabled: disabledProp}: ViafouraPreviewProps) {
  const {t} = useTranslation("liveblog");
  // Two independent reasons the trigger cannot work, and both must count. The prop covers the
  // editor's post stream, which renders rows directly with `isPreview`; the context covers the
  // in-editor widget preview, which renders the *reader* Liveblog and so has no `isPreview` to pass.
  //
  // The hook is read on its own line, not inlined into the `||`: short-circuiting it would make the
  // call conditional on the prop, which is a hook-order violation.
  const interactive = useViafouraInteractive();
  const disabled = disabledProp || !interactive;
  // The same two reasons gate the count: a card that will not open a conversation must not ask
  // Viafoura how many comments it has either.
  const count = useViafouraCommentCount(containerId, !disabled);
  // Zero is deliberately rendered as no number at all, matching the native preview's zero state
  // ("Comments", not "0 Comments"). An explicit zero on a call to action argues against taking it.
  const showCount = count !== undefined && count > 0;
  // Per-post, because a liveblog renders many of these and the ids must not collide.
  const noteId = `tik4-vfcp-note-${containerId}`;

  const handleOpen = useCallback(() => {
    if (disabled) {
      return;
    }
    openViafouraConversation(containerId);
    // Tracked on intent, not on a return value: the overlay is what provides Viafoura's mount node,
    // so it only appears *after* this call (design D21), and there is nothing meaningful to gate on.
    // Intent is also the truer reading — the reader did open the conversation, and whether Viafoura
    // renders into it is a separate matter (on a domain that is not allowlisted it never does).
    //
    // Viafoura engagement is tracked separately from native comments (c_opn / c_mor / a_cwr) so the
    // two providers stay comparable but distinguishable. Everything past the open happens inside
    // Viafoura's widget and is invisible to us.
    trackingFunction?.({t: "v_opn", e: containerId});
  }, [containerId, disabled, trackingFunction]);

  return <div className={`tik4-content-block tik4-content-block--vfcp ${position ? `tik4-content-block--position-${position}` : ''}`}>
    <div className="tik4-vfcp">
      <button
        className="tik4-vfcp__trigger tik4-button tik4-button--outline-dark"
        onClick={handleOpen}
        type="button"
        disabled={disabled}
        // Points at the visible note below rather than carrying a `title`. A `disabled` button is not
        // focusable, so a tooltip on it is unreachable by keyboard and unread by screen readers — the
        // explanation has to be in the accessibility tree and on screen, not in a hover affordance.
        aria-describedby={disabled ? noteId : undefined}
      >
          <MessageCircle className="tik4-vfcp__trigger__icon tik4-icon tik4-icon tik4-icon--mr" />
          <span className="tik4-vfcp__trigger__content">
            {showCount
              ? t("liveblog.viafoura.preview.open_with_count", {count})
              : t("liveblog.viafoura.preview.open")}
          </span>
      </button>
      {disabled && <p className="tik4-vfcp__note" id={noteId}>
        {t("liveblog.viafoura.preview.unavailable")}
      </p>}
    </div>
  </div>;
}

export default ViafouraPreview;
